import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Bell, UserPlus, UserCheck, Sparkles, Trophy, GraduationCap, Check, Calendar, Clock, Users, AlertTriangle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useNotificationSocket } from "@/hooks/useNotificationSocket";
import { formatDistanceToNow } from "date-fns";
import type { Notification } from "@shared/schema";

interface EnrichedNotification extends Notification {
  relatedUser?: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    profileImageUrl: string | null;
  } | null;
}

const notificationIcons: Record<string, typeof Bell> = {
  connection_request: UserPlus,
  connection_accepted: UserCheck,
  match_found: Sparkles,
  challenge_update: Trophy,
  course_complete: GraduationCap,
  event_reminder: Calendar,
  cohort_deadline: Clock,
  networking_intro: Users,
  deadline_approaching: AlertTriangle,
  category_article: FileText,
  article_published: FileText,
};

function getInitials(firstName: string | null, lastName: string | null): string {
  const first = firstName?.[0] || "";
  const last = lastName?.[0] || "";
  return (first + last).toUpperCase() || "?";
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [, setLocation] = useLocation();

  // Real-time notifications via WebSocket — auto-invalidates React Query caches
  // When WS is connected, notifications arrive instantly (< 1s).
  // The 30s polling below serves as a graceful fallback when WS is disconnected.
  useNotificationSocket();

  const { data: unreadCount = 0, isLoading: countLoading } = useQuery<number>({
    queryKey: ["/api/notifications/unread-count"],
    select: (data: any) => data.count ?? 0,
    refetchInterval: 30000, // Polling fallback — kept for WS disconnections
  });

  const { data: notifications = [], isLoading: notificationsLoading } = useQuery<EnrichedNotification[]>({
    queryKey: ["/api/notifications"],
    enabled: open,
  });

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("PATCH", `/api/notifications/${id}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", "/api/notifications/read-all");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
    },
  });

  const handleNotificationClick = async (notification: EnrichedNotification) => {
    if (!notification.isRead) {
      markReadMutation.mutate(notification.id);
    }
    setOpen(false);

    // Navigate based on notification type
    const type = notification.type;
    const entityType = notification.relatedEntityType;
    const entityId = notification.relatedEntityId;

    if (type === "connection_request" || type === "connection_accepted") {
      setLocation("/messages");
    } else if (type === "event_reminder") {
      setLocation("/events");
    } else if (type === "challenge_update") {
      setLocation(entityId ? `/challenges/${entityId}` : "/challenges");
    } else if (type === "course_complete") {
      setLocation(entityId ? `/academy/${entityId}` : "/academy");
    } else if (type === "category_article" || type === "article_published") {
      setLocation(entityId ? `/articles/${entityId}` : "/articles");
    } else if (type === "cohort_deadline") {
      setLocation(entityId ? `/cohort/${entityId}` : "/cohorts/apply");
    } else if (type === "networking_intro" || (type === "match_found" && entityType === "networking_intro")) {
      // Deep-link to the intro conversation if one exists (#85)
      const introId = entityId;
      if (introId) {
        try {
          const res = await fetch(`/api/networking/intros/${introId}/conversation`, { credentials: "include" });
          if (res.ok) {
            const data = await res.json();
            if (data.conversationId) {
              setLocation(`/messages?conv=${data.conversationId}`);
              return;
            }
          }
        } catch (_) { /* fallback below */ }
      }
      // Fallback: no conversation yet, go to intro requests
      setLocation("/network/intro-requests");
    } else if (type === "match_found") {
      setLocation("/networking");
    } else if (type === "deadline_approaching") {
      if (entityId && entityType === "article") {
        setLocation(`/articles/${entityId}`);
      } else if (entityType === "event") {
        setLocation("/events");
      } else if (entityId && entityType === "challenge") {
        setLocation(`/challenges/${entityId}`);
      } else if (entityId && entityType === "cohort") {
        setLocation(`/cohort/${entityId}`);
      }
    }
    // If no match, just close the popover (already done above)
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          data-testid="button-notification-bell"
        >
          <Bell className="h-5 w-5" />
          {!countLoading && unreadCount > 0 && (
            <Badge
              className="absolute -top-1 -right-1 h-5 min-w-5 px-1 flex items-center justify-center bg-purple-600 text-white border-0"
              data-testid="badge-unread-count"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="end"
        data-testid="popover-notifications"
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b">
          <h3 className="font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => markAllReadMutation.mutate()}
              disabled={markAllReadMutation.isPending}
              data-testid="button-mark-all-read"
            >
              <Check className="h-4 w-4 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="h-80">
          {notificationsLoading ? (
            <div className="p-4 space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-start gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground" data-testid="text-no-notifications">
              <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No notifications yet</p>
            </div>
          ) : (
            <div className="py-2">
              {notifications.map((notification) => {
                const IconComponent = notificationIcons[notification.type] || Bell;
                return (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left hover-elevate active-elevate-2 ${
                      !notification.isRead ? "bg-purple-50 dark:bg-purple-950/20" : ""
                    }`}
                    data-testid={`notification-item-${notification.id}`}
                  >
                    {notification.relatedUser ? (
                      <Avatar className="h-10 w-10">
                        <AvatarImage
                          src={notification.relatedUser.profileImageUrl || undefined}
                          alt=""
                        />
                        <AvatarFallback className="bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 text-xs">
                          {getInitials(
                            notification.relatedUser.firstName,
                            notification.relatedUser.lastName
                          )}
                        </AvatarFallback>
                      </Avatar>
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
                        <IconComponent className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notification.isRead ? "font-medium" : ""}`}>
                        {notification.title}
                      </p>
                      {notification.message && (
                        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                          {notification.message}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {notification.createdAt
                          ? formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })
                          : "Just now"}
                      </p>
                      {notification.dueAt && (
                        <p className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">
                          Due: {new Date(notification.dueAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                    {!notification.isRead && (
                      <div className="h-2 w-2 rounded-full bg-purple-600 mt-2" />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}